import { chartManager, metricsApi, type AvailableChart, type UserChartEntry } from '../../services/api';

const pending = new Map<string, Promise<boolean>>();
export function needsWeightDefault(setting: Record<string, unknown>): boolean {
  if (setting.metrics_default_initialized === true) return false;
  const selected = setting.selected_metrics;
  return !Array.isArray(selected) || selected.length === 0 || (selected.length === 1 && Number(selected[0]) === 0);
}
const read = (response: { data: { data: UserChartEntry[] } }) => response.data.data;

/** One-time default through existing chart APIs; explicit custom selections and later removals remain intact. */
export function ensureDefaultMetricsChart(userId: number, actorId: number, charts: UserChartEntry[], available: AvailableChart[]): Promise<boolean> {
  const key = `vo2-default-weight-chart-v1:${actorId}:${userId}`;
  try { if (localStorage.getItem(key) === 'done') return Promise.resolve(false); } catch { /* Storage is optional. */ }
  const existing = charts.find(chart => chart.chart_type === 'metrics_chart');
  const template = available.find(chart => chart.chart_type === 'metrics_chart');
  if (!existing && !template) return Promise.resolve(false);
  if (existing && !needsWeightDefault(existing.setting)) {
    try { localStorage.setItem(key, 'done'); } catch { /* No impact on server settings. */ }
    return Promise.resolve(false);
  }
  const previous = pending.get(key);
  if (previous) return previous;
  const operation = (async () => {
    const types = (await metricsApi.listTypes()).data.data.metrics;
    const weight = types.find(type => type.name.trim().toLowerCase() === 'weight');
    if (!weight) throw new Error('Weight metric is not in the API catalogue');
    let chart = existing;
    if (!chart && template) {
      await chartManager.addChart(template.id, userId);
      chart = read(await chartManager.getUserCharts(userId)).find(row => row.chart_type === 'metrics_chart');
    }
    if (!chart) throw new Error('Metric chart creation was not confirmed');
    await chartManager.updateChartSettings(chart.chart_id, { ...chart.setting, selected_metrics: [weight.id], metrics_default_initialized: true });
    const confirmed = read(await chartManager.getUserCharts(userId)).find(row => row.chart_id === chart!.chart_id);
    if (!confirmed || !Array.isArray(confirmed.setting.selected_metrics) || confirmed.setting.selected_metrics.length !== 1 || Number(confirmed.setting.selected_metrics[0]) !== weight.id) throw new Error('Weight chart settings were not confirmed');
    try { localStorage.setItem(key, 'done'); } catch { /* Server settings already retain the default marker. */ }
    return true;
  })().finally(() => pending.delete(key));
  pending.set(key, operation);
  return operation;
}
